import { useMemo } from "react";
import { Button, InputSwitch, Stack, Text } from "@bookingcom/bui-react";
import { CloseIcon } from "@bookingcom/bui-assets-react/streamline";
import type { Feature, FeatureOverrides } from "../../../types";
import styles from "./FeatureOverrideSheet.module.css";

export type FeatureOverrideListProps = {
  overrides: FeatureOverrides;
  onChange: (feature: Feature, enabled: boolean) => void;
  onRemove: (feature: Feature) => void;
};

export const FeatureOverrideList = ({ overrides, onChange, onRemove }: FeatureOverrideListProps) => {
  const overrideEntries = useMemo(
    () => Object.entries(overrides).sort(([featureA], [featureB]) => featureA.localeCompare(featureB)),
    [overrides]
  );

  return (
    <Stack gap={3}>
      <Text variant="strong_2">Overrides</Text>
      {overrideEntries.length === 0 && (
        <Text variant="body_2" color="neutral_alt">
          No feature flag overrides have been added.
        </Text>
      )}
      {overrideEntries.map(([feature, enabled]) => (
        <Stack
          key={feature}
          className={styles.featureFlagOverrideRow}
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          wrap="nowrap"
          gap={4}
        >
          <Text className={styles.featureOverrideName} attributes={{ title: feature }}>
            {feature}
          </Text>
          <Stack
            className={styles.featureOverrideActions}
            direction="row"
            alignItems="center"
            wrap="nowrap"
            gap={2}
          >
            <InputSwitch
              name={`featureOverride-${feature}`}
              ariaLabel={`${enabled ? "Disable" : "Enable"} ${feature}`}
              value={enabled}
              onChange={({ value }) => onChange(feature as Feature, value)}
            />
            <Button
              variant="tertiary-neutral"
              icon={CloseIcon}
              ariaLabel={`Remove override for ${feature}`}
              onClick={() => onRemove(feature as Feature)}
            />
          </Stack>
        </Stack>
      ))}
    </Stack>
  );
};
